import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Plus, Edit2, List, Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Checkbox } from '../components/ui';
import { getEntityId } from '../utils/entityId';
import {
  AttributePayload,
  deleteAttribute,
  fetchAttributes as loadAttributes,
  getApiErrorMessage,
  saveAttribute,
  slugifyAttributeName,
  uploadAdminImage,
} from '../services/adminCatalog';

interface Attribute {
  id: string;
  _id?: string;
  name: string;
  displayName: string;
  type: 'select' | 'color' | 'text';
  description?: string;
  iconUrl?: string;
  isRequired: boolean;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

type AttributeFormState = {
  name: string;
  displayName: string;
  type: 'select' | 'color' | 'text';
  description: string;
  iconUrl: string;
  isRequired: boolean;
  isActive: boolean;
  displayOrder: number;
  nameTouched: boolean;
};

const emptyForm = (displayOrder = 0): AttributeFormState => ({
  name: '',
  displayName: '',
  type: 'select',
  description: '',
  iconUrl: '',
  isRequired: false,
  isActive: true,
  displayOrder,
  nameTouched: false,
});

const Attributes: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);
  const [formData, setFormData] = useState<AttributeFormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const fetchAttributes = async () => {
    try {
      setLoading(true);
      setAttributes(await loadAttributes<Attribute>());
    } catch (error: unknown) {
      console.error('Error fetching attributes:', error);
      setAttributes([]);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'attributes' })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  const filteredAttributes = attributes.filter(
    (attribute) =>
      searchTerm === '' ||
      attribute.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attribute.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attribute.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreateModal = () => {
    setEditingAttribute(null);
    setFormData(emptyForm(attributes.length));
    setShowModal(true);
  };

  const openEditModal = (attribute: Attribute) => {
    setEditingAttribute(attribute);
    setFormData({
      name: attribute.name,
      displayName: attribute.displayName,
      type: attribute.type,
      description: attribute.description || '',
      iconUrl: attribute.iconUrl || '',
      isRequired: attribute.isRequired,
      isActive: attribute.isActive,
      displayOrder: attribute.displayOrder ?? 0,
      nameTouched: true,
    });
    setShowModal(true);
  };

  const handleDisplayNameChange = (displayName: string) => {
    setFormData((prev) => ({
      ...prev,
      displayName,
      name: prev.nameTouched ? prev.name : slugifyAttributeName(displayName),
    }));
  };

  const handleIconUpload = async (file: File) => {
    setUploadingIcon(true);
    try {
      const fileUrl = await uploadAdminImage(file);
      setFormData((prev) => ({ ...prev, iconUrl: fileUrl }));
    } catch (error: unknown) {
      alert(getApiErrorMessage(error, t('messages.uploadFailed', { ns: 'attributes' })));
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.displayName.trim() || !formData.name.trim()) return;

    setSubmitting(true);
    try {
      const payload: AttributePayload = {
        name: formData.name.trim().toLowerCase(),
        displayName: formData.displayName.trim(),
        type: formData.type,
        description: formData.description.trim() || undefined,
        iconUrl: formData.iconUrl.trim() || undefined,
        isRequired: formData.isRequired,
        isActive: formData.isActive,
        displayOrder: formData.displayOrder,
      };

      const attributeId = editingAttribute ? getEntityId(editingAttribute) : undefined;
      await saveAttribute(payload, attributeId);
      setShowModal(false);
      setEditingAttribute(null);
      setFormData(emptyForm());
      await fetchAttributes();
    } catch (error: unknown) {
      console.error('Error saving attribute:', error);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'attributes' })));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (attribute: Attribute) => {
    if (window.confirm(t('messages.confirmDelete', { ns: 'attributes', name: attribute.displayName }))) {
      try {
        const attributeId = getEntityId(attribute);
        if (!attributeId) return;
        await deleteAttribute(attributeId);
        await fetchAttributes();
      } catch (error: unknown) {
        console.error('Error deleting attribute:', error);
        alert(getApiErrorMessage(error, t('messages.errorDeleting', { ns: 'attributes' })));
      }
    }
  };

  const handleToggleStatus = async (attribute: Attribute) => {
    const attributeId = getEntityId(attribute);
    if (!attributeId) return;
    try {
      await saveAttribute(
        {
          name: attribute.name,
          displayName: attribute.displayName,
          type: attribute.type,
          description: attribute.description,
          iconUrl: attribute.iconUrl,
          isRequired: attribute.isRequired,
          isActive: !attribute.isActive,
          displayOrder: attribute.displayOrder,
        },
        attributeId
      );
      await fetchAttributes();
    } catch (error: unknown) {
      console.error('Error updating attribute status:', error);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'attributes' })));
    }
  };

  const AttributeCard: React.FC<{ attribute: Attribute }> = ({ attribute }) => {
    const getTypeColor = (type: string) => {
      switch (type) {
        case 'color':
          return 'bg-purple-100 text-purple-800';
        case 'select':
          return 'bg-blue-100 text-blue-800';
        case 'text':
          return 'bg-gray-100 text-gray-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const getTypeIcon = (type: string) => {
      switch (type) {
        case 'color':
          return '🎨';
        case 'select':
          return '📋';
        case 'text':
          return '📝';
        default:
          return '📋';
      }
    };

    const attributeId = getEntityId(attribute);

    return (
      <div className="card p-6 hover:shadow-card-hover transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3 flex-1">
            <div className="w-12 h-12 bg-gradient-to-br from-custom-primary to-custom-secondary rounded-lg flex items-center justify-center text-custom-charcoal text-xl flex-shrink-0 overflow-hidden">
              {attribute.iconUrl ? (
                <img src={attribute.iconUrl} alt="" className="w-full h-full object-contain p-1" />
              ) : (
                getTypeIcon(attribute.type)
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="font-semibold text-gray-900 text-lg truncate">{attribute.displayName}</h3>
              </div>
              <p className="text-xs text-gray-500 font-mono mb-2">{attribute.name}</p>
              {attribute.description && (
                <p className="text-gray-600 text-sm line-clamp-2">{attribute.description}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-3">
          <div className="flex items-center flex-wrap gap-2">
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(attribute.type)}`}>
              {attribute.type}
            </span>
            <span className={`badge-${attribute.isActive ? 'green' : 'red'}`}>
              {attribute.isActive ? t('badges.active', { ns: 'attributes' }) : t('badges.inactive', { ns: 'attributes' })}
            </span>
            {attribute.isRequired && (
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                {t('badges.required', { ns: 'attributes' })}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {t('badges.order', { ns: 'attributes' })}: {attribute.displayOrder}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => handleToggleStatus(attribute)}
                variant={attribute.isActive ? 'outline' : 'secondary'}
                size="sm"
              >
                {attribute.isActive ? t('buttons.deactivate', { ns: 'common' }) : t('buttons.activate', { ns: 'common' })}
              </Button>
              <button
                onClick={() => openEditModal(attribute)}
                className="text-custom-primary hover:text-custom-primary-hover p-1"
                title={t('editAttribute', { ns: 'attributes' })}
              >
                <Edit2 size={16} />
              </button>
              {attribute.type !== 'text' && attributeId && (
                <Button
                  onClick={() => navigate(`/attributes/${attributeId}/values`)}
                  variant="outline"
                  size="sm"
                  icon={List}
                >
                  {t('manageValues', { ns: 'attributes' })}
                </Button>
              )}
            </div>
            <button
              onClick={() => handleDelete(attribute)}
              className="text-red-600 hover:text-red-700 p-1"
              title={t('buttons.delete', { ns: 'common', defaultValue: 'Delete' })}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'attributes' })}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'attributes' })}</p>
        </div>
        <Button onClick={openCreateModal} icon={Plus} variant="primary">
          {t('addAttribute', { ns: 'attributes' })}
        </Button>
      </div>

      <div className="card p-6">
        <div className="max-w-md">
          <Input
            type="text"
            placeholder={t('searchPlaceholder', { ns: 'attributes' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
            variant="search"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-primary"></div>
          </div>
        ) : filteredAttributes.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchTerm ? t('messages.noAttributesMatchingSearch', { ns: 'attributes' }) : t('messages.noAttributesFound', { ns: 'attributes' })}
          </div>
        ) : (
          filteredAttributes.map((attribute, index) => (
            <AttributeCard key={getEntityId(attribute) || `attribute-${index}`} attribute={attribute} />
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingAttribute ? t('editAttribute', { ns: 'attributes' }) : t('createAttribute', { ns: 'attributes' })}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('fields.displayName', { ns: 'attributes' }) + ' *'}
                type="text"
                value={formData.displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                placeholder={t('fields.displayNamePlaceholder', { ns: 'attributes' })}
                required
              />

              <div>
                <Input
                  label={t('fields.internalName', { ns: 'attributes' }) + ' *'}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value, nameTouched: true })
                  }
                  placeholder={t('fields.internalNamePlaceholder', { ns: 'attributes' })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('fields.internalNameHelp', { ns: 'attributes' })}</p>
              </div>

              <Select
                label={t('fields.type', { ns: 'attributes' })}
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value as AttributeFormState['type'] })
                }
                options={[
                  { value: 'select', label: t('fields.selectType', { ns: 'attributes' }) },
                  { value: 'color', label: t('fields.colorType', { ns: 'attributes' }) },
                  { value: 'text', label: t('fields.textType', { ns: 'attributes' }) },
                ]}
              />
              <p className="text-xs text-gray-500 -mt-2">{t('fields.typeHelp', { ns: 'attributes' })}</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.icon', { ns: 'attributes' })}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-4 text-center">
                  {formData.iconUrl ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.iconUrl}
                        alt=""
                        className="max-h-20 rounded-lg mx-auto object-contain"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        onClick={() => setFormData((prev) => ({ ...prev, iconUrl: '' }))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <Upload className="mx-auto text-gray-400 mb-2" size={24} />
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    id="attribute-icon-upload"
                    disabled={uploadingIcon}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleIconUpload(file);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor="attribute-icon-upload"
                    className="text-sm text-custom-primary cursor-pointer hover:underline"
                  >
                    {uploadingIcon
                      ? t('fields.uploading', { ns: 'attributes' })
                      : t('fields.uploadIcon', { ns: 'attributes' })}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.description', { ns: 'attributes' })}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                  placeholder={t('fields.descriptionPlaceholder', { ns: 'attributes' })}
                />
              </div>

              <Input
                label={t('fields.displayOrder', { ns: 'attributes' })}
                type="number"
                value={formData.displayOrder}
                onChange={(e) =>
                  setFormData({ ...formData, displayOrder: parseInt(e.target.value, 10) || 0 })
                }
                min={0}
              />
              <p className="text-xs text-gray-500 -mt-2">{t('fields.displayOrderHelp', { ns: 'attributes' })}</p>

              <Checkbox
                id="attribute-isRequired"
                checked={formData.isRequired}
                onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                label={t('fields.requiredByDefault', { ns: 'attributes' })}
                variant="default"
              />

              <Checkbox
                id="attribute-isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                label={t('fields.active', { ns: 'attributes' })}
                variant="default"
              />

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} variant="outline" disabled={submitting}>
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.displayName.trim() || !formData.name.trim()}
                  variant="primary"
                  loading={submitting}
                >
                  {editingAttribute ? t('buttons.saveChanges', { ns: 'common' }) : t('buttons.add', { ns: 'common' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attributes;
